import {
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { CatalogTypeSection } from "@modules";
import { Store } from "lucide-react";

import type { IHubManagerCatalogSection } from "../useHubManagerCatalogSections";

interface IMyCatalogsProps {
  sections: IHubManagerCatalogSection[];
  sectionsLoading: boolean;
  selectedForExport?: Set<string>;
  onExportToggle?: (catalogId: number, version: number) => void;
}

export function MyCatalogs({
  sections,
  sectionsLoading,
  selectedForExport,
  onExportToggle,
}: IMyCatalogsProps) {
  const { t } = useTranslation();

  if (sectionsLoading) {
    return (
      <Card
        variant="content"
        className="min-h-0 min-w-0 flex-1 border-0 shadow-none"
        isLoading>
        <CardContent />
      </Card>
    );
  }

  return (
    <ScrollArea
      className="min-h-0 min-w-0 flex-1"
      viewportClassName="flex flex-col gap-8 overscroll-y-contain pb-2 [-webkit-overflow-scrolling:touch]">
      {sections.length === 0 ? (
        <Empty className="mx-auto min-h-0 max-w-3xl flex-1">
          <EmptyMedia>
            <Store className="h-6 w-6 text-muted-foreground" />
          </EmptyMedia>
          <EmptyHeader>
            <EmptyTitle>
              {t("hub.catalog_manager.my_catalogs_empty", {
                defaultValue: "My Catalogs are empty.",
              })}
            </EmptyTitle>
            <EmptyDescription>
              {t("hub.catalog_manager.no_catalogs", {
                defaultValue: "No catalogs yet. Be the first to contribute!",
              })}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        sections.map((section, idx) => (
          <CatalogTypeSection
            key={`${section.catalogType.typeId ?? section.catalogType.name ?? "type"}-${idx}`}
            typeDescription={section.catalogType.description}
            typeLabel={section.catalogType.label}
            hubManagerTab="myCatalogs"
            catalogs={section.catalogs}
            totalCount={section.totalCount}
            isLoading={section.isLoading}
            onViewAll={section.onViewAll}
            selectedForExport={selectedForExport}
            onExportToggle={onExportToggle}
          />
        ))
      )}
    </ScrollArea>
  );
}
